import * as React from 'react';

import {
  AddressType,
  enumAddressType,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, Checkbox, Separator, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { AddressFormState as SharedAddressFormState } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { useCreateAccountAddressMutation } from '../../hooks/mutations/useCreateAccountAddressMutation';
import { useCreateAddressPermissionQuery } from '../../hooks/queries/useCreateAddressPermissionQuery';
import { $serviceForm } from '../../state/serviceForm.localState';
import { ServiceFormState } from '../../types';

import { AccountSelect } from './AccountSelect';
import { LocationSelect, NewLocationArgs } from './LocationSelect';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { StreetAddressField } from '~/shared/components/StreetAddressField';

type AddressFormState = NonNullable<ServiceFormState['address']>;

const DEFAULT_ADDRESS: AddressFormState = {
  streetAddress: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  addressType: enumAddressType.Standard,
  isMailingAddress: false,
};

interface ManualAddressFormProps {
  address: AddressFormState | undefined;
  onFieldChange: <K extends keyof AddressFormState>(
    field: K,
  ) => (value: AddressFormState[K]) => void;
  onAddressChange: (address: AddressFormState) => void;
  onCancel: () => void;
  isReadOnly?: boolean;
  accountId?: string | null;
  isCreatingLocation: boolean;
  handleSaveLocation: () => Promise<void>;
  isExisting: boolean;
  onSelectExistingLocation: () => void;
}

const ManualAddressForm = ({
  address,
  onFieldChange,
  onAddressChange,
  onCancel,
  isReadOnly,
  accountId,
  isCreatingLocation,
  handleSaveLocation,
  isExisting = false,
  onSelectExistingLocation,
}: ManualAddressFormProps) => {
  const t = useTranslations();
  const [saveLocationCheckbox, setSaveLocationCheckbox] = React.useState(false);
  const [formValues, setFormValues] = useAtom($serviceForm);
  const { data: permission } = useCreateAddressPermissionQuery({});
  const canCreateAddress = permission?.allowed && !isExisting && !isReadOnly;

  return (
    <div className="@ManualAddressForm space-y-4 rounded-lg border border-neutral/20 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-medium">
          {isExisting
            ? t.servicesView.form.fields.existingLocation
            : t.servicesView.form.fields.newLocation}
        </h2>
        {!isReadOnly &&
          (isExisting ? (
            <Button onPress={onSelectExistingLocation} variant="link">
              {t.servicesView.form.buttons.remove}
            </Button>
          ) : (
            <Button onClick={onCancel} variant="link">
              {t.servicesView.form.buttons.cancel}
            </Button>
          ))}
      </div>
      <SelectEnum<AddressType>
        enumValue={enumAddressType}
        label={t.servicesView.form.fields.address.type}
        value={address?.addressType ?? enumAddressType.Standard}
        onChange={(key) =>
          onFieldChange('addressType')(key as AddressFormState['addressType'])
        }
        isRequired
        isDisabled={isReadOnly}
      />

      {canCreateAddress && (
        <div className="flex justify-end">
          <Checkbox
            isSelected={saveLocationCheckbox}
            onChange={(checked) => setSaveLocationCheckbox(checked)}
            className="text-sm"
          >
            {t.servicesView.form.fields.address.saveLocation}
          </Checkbox>
        </div>
      )}
      <StreetAddressField
        value={{
          streetAddress: address?.streetAddress ?? '',
          city: address?.city ?? '',
          state: address?.state ?? '',
          postalCode: address?.postalCode ?? '',
          country: address?.country ?? '',
        }}
        onChange={(sharedAddress: SharedAddressFormState) => {
          onAddressChange({
            ...address,
            ...sharedAddress,
            addressType: address?.addressType ?? enumAddressType.Standard,
            isMailingAddress: address?.isMailingAddress ?? false,
          });
        }}
        isRequired
        isReadOnly={isReadOnly}
      />

      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.servicesView.form.fields.address.city}
          placeholder={t.servicesView.form.placeholders.city}
          value={address?.city}
          onChange={onFieldChange('city')}
          isRequired
          isDisabled={isReadOnly}
        />
        <TextField
          label={t.servicesView.form.fields.address.state}
          placeholder={t.servicesView.form.placeholders.state}
          value={address?.state}
          onChange={onFieldChange('state')}
          isRequired
          isDisabled={isReadOnly}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t.servicesView.form.fields.address.postalCode}
          placeholder={t.servicesView.form.placeholders.postalCode}
          value={address?.postalCode}
          onChange={onFieldChange('postalCode')}
          isRequired
          isDisabled={isReadOnly}
        />
        <TextField
          label={t.servicesView.form.fields.address.country}
          placeholder={t.servicesView.form.placeholders.country}
          value={address?.country}
          onChange={onFieldChange('country')}
          isRequired
          isDisabled={isReadOnly}
        />
      </div>
      <AccountSelect
        label={t.servicesView.form.fields.address.account}
        value={accountId ?? null}
        onChange={(account) => {
          setFormValues({
            ...formValues,
            accountId: account?.id ?? null,
          });
        }}
        isDisabled={isReadOnly}
      />
      {saveLocationCheckbox && canCreateAddress && (
        <>
          <Separator />
          <TextField
            label={t.servicesView.form.fields.address.locationName}
            placeholder={t.servicesView.form.placeholders.addressName}
            value={address?.name ?? ''}
            onChange={onFieldChange('name')}
            isRequired
            isDisabled={isReadOnly}
          />
          <div className="flex flex-col gap-2">
            <Button onPress={handleSaveLocation} isLoading={isCreatingLocation}>
              {t.servicesView.form.buttons.saveAndAddLocation}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

interface AddressSectionProps {
  onUpdate: (address: AddressFormState | undefined) => void;
  onFieldChange: (
    field: keyof ServiceFormState,
  ) => (value: string | null) => void;
  isReadOnly?: boolean;
}

export const AddressSection = ({
  onUpdate,
  onFieldChange,
  isReadOnly,
}: AddressSectionProps) => {
  const t = useTranslations();
  const refetchLocationsRef = React.useRef<(() => void) | undefined>(undefined);
  const [isCreatingManualAddress, setIsCreatingManualAddress] =
    React.useState(false);
  const [formValues, setFormValues] = useAtom($serviceForm);
  const { address, facilityGroupId, accountId, accountAddressId } = formValues;
  const toast = useToast();

  const resetAddress = () => {
    onUpdate(DEFAULT_ADDRESS);
  };

  const handleCancel = () => {
    setIsCreatingManualAddress(false);
    resetAddress();
    onFieldChange('accountId')(null);
    onFieldChange('accountAddressId')(null);
  };

  const handleFieldChange =
    <K extends keyof AddressFormState>(field: K) =>
    (value: AddressFormState[K]) => {
      const currentAddress = address ?? DEFAULT_ADDRESS;

      onUpdate({
        ...currentAddress,
        [field]: value,
      });
    };

  const handleSelectLocation = (args: NewLocationArgs) => {
    setIsCreatingManualAddress(false);

    if (args.type === 'account' && args.accountId) {
      onFieldChange('accountId')(args.accountId);
      onFieldChange('accountAddressId')(args.accountAddressId ?? null);
    } else if (args.type === 'facility') {
      onFieldChange('accountId')(null);
      onFieldChange('accountAddressId')(null);
    }

    onUpdate(undefined);
  };

  const handleAddNewLocation = () => {
    setIsCreatingManualAddress(true);
    if (!address?.streetAddress) {
      resetAddress();
    }
    onFieldChange('accountAddressId')(null);
  };

  const { mutateAsync: createAccountAddress, isPending: isSavingLocation } =
    useCreateAccountAddressMutation();

  const handleSaveLocation = async () => {
    if (!formValues.accountId || !address) {
      return;
    }

    try {
      const { name: locationName, ...addressFields } = address;
      const { crmAccountAddressCreate } = await createAccountAddress({
        accountId: formValues.accountId,
        name: locationName ?? '',
        address: addressFields,
      });

      setFormValues({
        ...formValues,
        accountAddressId: crmAccountAddressCreate.id,
      });

      // Refetch locations to update the location list
      refetchLocationsRef.current?.();

      setIsCreatingManualAddress(false);
      toast.success(t.servicesView.form.messages.locationSaved);
    } catch (error) {
      console.error(error);
      toast.error(t.servicesView.form.messages.locationError);
    }
  };

  const selectedLocationId = accountAddressId ?? null;
  const hasLinkedLocation = !!accountAddressId || !!formValues.facilityId;
  const hasInlineAddress = !!address?.streetAddress && !hasLinkedLocation;
  const showAddressForm = isCreatingManualAddress || hasInlineAddress;

  return (
    <div className="@AddressSection space-y-4">
      <LocationSelect
        label={t.servicesView.form.fields.address.location}
        value={selectedLocationId ?? undefined}
        isDisabled={isReadOnly || showAddressForm}
        facilityGroupId={facilityGroupId ?? undefined}
        onAddNewLocation={handleAddNewLocation}
        onSelectLocation={handleSelectLocation}
        onRefetchLocations={(refetch) => {
          refetchLocationsRef.current = refetch;
        }}
      />
      {showAddressForm && (
        <ManualAddressForm
          address={address}
          isExisting={hasLinkedLocation}
          onFieldChange={handleFieldChange}
          onAddressChange={onUpdate}
          onCancel={handleCancel}
          isReadOnly={isReadOnly}
          accountId={accountId}
          isCreatingLocation={isSavingLocation}
          handleSaveLocation={handleSaveLocation}
          onSelectExistingLocation={handleCancel}
        />
      )}
    </div>
  );
};
